/** Edit a draft invoice. */
import { useMemo, useState, type ChangeEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { type InvoiceResponse, type OfferResponse } from "@offertli/shared";
import { Button } from "../../components/ui/Button.js";
import { InputText } from "@primereact/ui/inputtext";
import { Textarea } from "@primereact/ui/textarea";
import { apiGet, apiPatch } from "../../lib/api.js";
import { Field } from "../../components/ui/Field.js";
import { MutationError } from "../shared.js";
import { Panel } from "../commercial/document.js";
import {
  DiscountFields,
  LineEditor,
  TotalsPanel,
  emptyLine,
  linesAreValid,
  linesPayload,
  liveTotals,
  toEditableLines,
  toTotalsLike,
  useCommercialSettings,
  type EditableLine,
} from "../commercial/lines.js";

export function InvoiceEditor({ invoice }: { invoice: InvoiceResponse }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const settings = useCommercialSettings();
  const offer = useQuery({
    queryKey: ["offer", invoice.offer?.id],
    queryFn: () =>
      apiGet<{ data: OfferResponse }>(`/offers/${invoice.offer!.id}`).then(
        (response) => response.data,
      ),
    enabled: Boolean(invoice.offer),
  });
  const [title, setTitle] = useState(invoice.title ?? "");
  const [introText, setIntroText] = useState(invoice.introText ?? "");
  const [termsText, setTermsText] = useState(invoice.paymentTermsText ?? "");
  const [termDays, setTermDays] = useState(String(invoice.paymentTermDays));
  const [billing, setBilling] = useState(invoice.billing);
  const [discount, setDiscount] = useState({
    percent: invoice.discountPercent ?? "",
    amount: invoice.discountAmount ?? "",
  });
  const [lines, setLines] = useState<EditableLine[]>(() =>
    invoice.items.length
      ? toEditableLines(invoice.items)
      : [emptyLine(settings.data?.defaultTaxRate)],
  );
  const [dirty, setDirty] = useState(false);
  const touch =
    <T,>(setter: (value: T) => void) =>
    (value: T) => {
      setter(value);
      setDirty(true);
    };
  // Max per offer line = still open on the offer + what this draft holds.
  const maxBySource = useMemo(() => {
    if (!offer.data?.remainingToInvoice) return undefined;
    const own = new Map<string, number>();
    for (const item of invoice.items)
      if (item.sourceOfferItemId)
        own.set(
          item.sourceOfferItemId,
          (own.get(item.sourceOfferItemId) ?? 0) + Number(item.quantity),
        );
    return Object.fromEntries(
      offer.data.remainingToInvoice.map((line) => [
        line.offerItemId,
        String(Number(line.quantity) + (own.get(line.offerItemId) ?? 0)),
      ]),
    );
  }, [offer.data, invoice.items]);
  const totals = useMemo(
    () => liveTotals(lines, discount, settings.data),
    [lines, discount, settings.data],
  );
  const save = useMutation({
    mutationFn: () =>
      apiPatch<{ data: InvoiceResponse }>(`/invoices/${invoice.id}`, {
        version: invoice.version,
        title: title.trim() || null,
        introText,
        paymentTermsText: termsText,
        paymentTermDays: Number(termDays) || 0,
        billing,
        discountPercent: discount.percent.trim() || null,
        discountAmount: discount.amount.trim() || null,
        items: linesPayload(lines),
      }),
    onSuccess: (response) => {
      queryClient.setQueryData(["invoice", invoice.id], response.data);
      void queryClient.invalidateQueries({ queryKey: ["invoices"] });
      setDirty(false);
    },
  });
  const showTax = settings.data?.vatRegistered !== false;
  const addressField = (
    key: "name" | "company" | "line1" | "line2" | "postalCode" | "city",
    label: string,
    className?: string,
  ) => (
    <Field label={label} className={className}>
      <InputText
        value={billing[key] ?? ""}
        onChange={(event: ChangeEvent<HTMLInputElement>) =>
          touch(setBilling)({ ...billing, [key]: event.target.value })
        }
      />
    </Field>
  );
  return (
    <form
      className="space-y-4"
      onSubmit={(event) => {
        event.preventDefault();
        save.mutate();
      }}
    >
      <Panel>
        <Field label={t("common.title")}>
          <InputText
            value={title}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              touch(setTitle)(event.target.value)
            }
          />
        </Field>
        <Field label={t("doc.introText")} className="mt-4">
          <Textarea
            value={introText}
            rows={2}
            autoResize
            onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
              touch(setIntroText)(event.target.value)
            }
          />
        </Field>
      </Panel>
      <Panel title={t("lines.heading")}>
        <LineEditor
          lines={lines}
          onChange={touch(setLines)}
          currency={invoice.currency}
          settings={settings.data}
          maxQuantityBySource={maxBySource}
        />
      </Panel>
      <Panel title={t("totals.heading")}>
        <div className="grid gap-6 md:grid-cols-2">
          <DiscountFields
            percent={discount.percent}
            amount={discount.amount}
            onChange={touch(setDiscount)}
            currency={invoice.currency}
          />
          <TotalsPanel
            totals={toTotalsLike(totals)}
            currency={invoice.currency}
            showTax={showTax}
            totalLabel={
              invoice.kind === "credit_note"
                ? t("invoice.creditTotal")
                : undefined
            }
          />
        </div>
      </Panel>
      <Panel title={t("invoice.billingAddress")}>
        <div className="grid gap-3 sm:grid-cols-6">
          {addressField("company", t("customer.companyName"), "sm:col-span-3")}
          {addressField("name", t("invoice.contactName"), "sm:col-span-3")}
          {addressField("line1", t("common.address"), "sm:col-span-4")}
          {addressField("line2", t("common.address2"), "sm:col-span-2")}
          {addressField("postalCode", t("common.postalCode"), "sm:col-span-2")}
          {addressField("city", t("common.city"), "sm:col-span-4")}
        </div>
      </Panel>
      {invoice.kind === "invoice" ? (
        <Panel title={t("invoice.paymentTerms")}>
          <div className="grid gap-4 sm:grid-cols-[10rem_1fr]">
            <Field label={t("invoice.termDays")}>
              <InputText
                inputMode="numeric"
                value={termDays}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  touch(setTermDays)(event.target.value.replace(/\D/g, ""))
                }
              />
            </Field>
            <Field label={t("doc.termsText")}>
              <Textarea
                value={termsText}
                rows={2}
                autoResize
                onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
                  touch(setTermsText)(event.target.value)
                }
              />
            </Field>
          </div>
        </Panel>
      ) : null}
      <MutationError error={save.error} />
      <div className="sticky bottom-16 z-10 flex items-center justify-between gap-3 rounded-xl border border-app-border bg-app-surface/95 px-4 py-3 shadow-sm backdrop-blur md:bottom-0">
        <span className="text-sm text-app-muted">
          {dirty ? t("doc.unsaved") : t("doc.saved")}
        </span>
        <Button
          type="submit"
          loading={save.isPending || undefined}
          disabled={!dirty || !linesAreValid(lines)}
        >
          {t("common.save")}
        </Button>
      </div>
    </form>
  );
}
